import { getCountryForTimezone } from "countries-and-timezones";
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  isSupportedCountry,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";

import type { CountryCode } from "libphonenumber-js";

export interface CountryOption {
  code: CountryCode;
  callingCode: string;
  name: string;
}

export interface ParsedPhoneNumber {
  country: CountryCode;
  nationalNumber: string;
}

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

function getCountryName(code: CountryCode): string {
  try {
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
}

// Flag emoji (regional indicator symbols) do not render on Windows, so we load
// flag images from a CDN instead. Returns [1x, 2x] URLs for a crisp retina
// rendering.
export function flagImageUrls(code: string): { src: string; srcSet: string } {
  const iso = code.toLowerCase();
  return {
    src: `https://flagcdn.com/w20/${iso}.webp`,
    srcSet: `https://flagcdn.com/w40/${iso}.webp 2x`,
  };
}

export const COUNTRY_OPTIONS: CountryOption[] = getCountries()
  .map((code) => ({
    code,
    callingCode: getCountryCallingCode(code),
    name: getCountryName(code),
  }))
  .sort((first, second) => first.name.localeCompare(second.name));

const COUNTRY_OPTIONS_BY_CODE = new Map(
  COUNTRY_OPTIONS.map((option) => [option.code, option]),
);

export function getCountryOption(code: CountryCode): CountryOption | undefined {
  return COUNTRY_OPTIONS_BY_CODE.get(code);
}

export interface PhoneNumberErrorOptions {
  required?: boolean;
}

const INVALID_PHONE_MESSAGE = "Enter a valid phone number.";
const REQUIRED_PHONE_MESSAGE = "Phone number is required.";

// Single source of truth for phone-field validity + message. Returns null when
// the value is valid (or empty and optional), otherwise the error to display.
// Callers decide when to surface it (on blur, on submit) and whether to block.
// Copy is neutral guidance only and reveals nothing about existing accounts.
export function getPhoneNumberError(
  value: string,
  options: PhoneNumberErrorOptions = {},
): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return options.required ? REQUIRED_PHONE_MESSAGE : null;
  }
  return isValidPhoneNumber(trimmed) ? null : INVALID_PHONE_MESSAGE;
}

// Normalizes a value emitted by PhoneNumberField (a formatted international
// string) to E.164 for the backend. Parses with libphonenumber-js and returns
// its E.164 form; unparseable input (empty, or malformed like "+1+555…")
// yields "" rather than a partially-normalized string.
export function toE164(phoneNumber: string): string {
  const parsed = parsePhoneNumberFromString(phoneNumber);
  return parsed ? parsed.number : "";
}

// Best-effort guess of the user's country so the dial code defaults sensibly.
// Prefers the browser timezone, which reflects the machine's actual location,
// over browser language (which is a language preference, not a location).
// Falls back to locale, then the provided default. The number-based detection
// still corrects the flag once the user types their number.
export function detectDefaultCountry(
  fallback: CountryCode = "US",
): CountryCode {
  try {
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const country = timeZone ? getCountryForTimezone(timeZone) : null;
    if (country && isSupportedCountry(country.id)) {
      return country.id;
    }
  } catch {
    // Fall through to locale detection.
  }
  try {
    const { region } = new Intl.Locale(navigator.language).maximize();
    if (region && isSupportedCountry(region)) {
      return region;
    }
  } catch {
    // Older browsers may lack Intl.Locale / maximize — fall back below.
  }
  return fallback;
}

// Parses a stored phone value (E.164 or international format, e.g. "+1 555 123
// 4567") back into a country + national number so the split fields can be
// pre-populated. Falls back to the default country when the value is empty or
// cannot be attributed to a specific country.
export function parsePhoneValue(
  value: string,
  defaultCountry: CountryCode,
): ParsedPhoneNumber {
  const trimmed = value.trim();
  if (trimmed) {
    const parsed = parsePhoneNumberFromString(trimmed);
    if (parsed?.country) {
      return { country: parsed.country, nationalNumber: parsed.nationalNumber };
    }
    const parsedWithDefault = parsePhoneNumberFromString(
      trimmed,
      defaultCountry,
    );
    if (parsedWithDefault) {
      return {
        country: parsedWithDefault.country ?? defaultCountry,
        nationalNumber: parsedWithDefault.nationalNumber,
      };
    }
  }
  return { country: defaultCountry, nationalNumber: "" };
}

// Combines the selected country and the typed national digits into a single
// international-formatted string (e.g. "+1 555 123 4567"), matching the value
// shape the rest of the app already stores and normalizes to E.164.
export function buildPhoneValue(
  country: CountryCode,
  nationalNumber: string,
): string {
  const digits = nationalNumber.replace(/\D/g, "");
  if (!digits) {
    return "";
  }
  return new AsYouType(country).input(
    `+${getCountryCallingCode(country)}${digits}`,
  );
}

// Formats just the national portion for display in the number field, grouped
// according to the selected country (e.g. "555 123 4567" for US, "20 7946
// 0958" for GB). Derived from the international value so the field and the
// stored value always use the same grouping, and there is no hard-coded
// per-country mask to conflict.
export function formatNationalNumber(
  country: CountryCode,
  nationalNumber: string,
): string {
  const international = buildPhoneValue(country, nationalNumber);
  if (!international) {
    return "";
  }
  const callingCode = getCountryCallingCode(country);
  return international.replace(new RegExp(`^\\+${callingCode}\\s*`), "");
}
